import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { cn } from "@apex/shared";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = "text", error, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          "apex-flex apex-h-10 apex-w-full apex-rounded-md apex-border apex-border-input apex-bg-background apex-px-3 apex-py-2 apex-text-sm apex-ring-offset-background",
          "file:apex-border-0 file:apex-bg-transparent file:apex-text-sm file:apex-font-medium",
          "placeholder:apex-text-muted-foreground",
          "focus-visible:apex-outline-none focus-visible:apex-ring-2 focus-visible:apex-ring-ring focus-visible:apex-ring-offset-2",
          "disabled:apex-cursor-not-allowed disabled:apex-opacity-50",
          error && "apex-border-destructive focus-visible:apex-ring-destructive",
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

Input.displayName = "Input";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, error, rows = 3, ...props }, ref) => {
    return (
      <textarea
        rows={rows}
        className={cn(
          "apex-flex apex-w-full apex-rounded-md apex-border apex-border-input apex-bg-background apex-px-3 apex-py-2 apex-text-sm apex-ring-offset-background",
          "placeholder:apex-text-muted-foreground",
          "focus-visible:apex-outline-none focus-visible:apex-ring-2 focus-visible:apex-ring-ring focus-visible:apex-ring-offset-2",
          "disabled:apex-cursor-not-allowed disabled:apex-opacity-50",
          "apex-resize-y apex-min-h-[80px]",
          error && "apex-border-destructive focus-visible:apex-ring-destructive",
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

Textarea.displayName = "Textarea";

export interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean;
}

export const Label = forwardRef<HTMLLabelElement, LabelProps>(
  ({ className, required, children, ...props }, ref) => (
    <label
      ref={ref}
      className={cn(
        "apex-text-sm apex-font-medium apex-leading-none peer-disabled:apex-cursor-not-allowed peer-disabled:apex-opacity-70",
        className
      )}
      {...props}
    >
      {children}
      {required && <span className="apex-text-destructive apex-ml-1">*</span>}
    </label>
  )
);

Label.displayName = "Label";

export interface FormFieldProps {
  label?: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}

export function FormField({
  label,
  error,
  required,
  children,
  className,
}: FormFieldProps) {
  return (
    <div className={cn("apex-space-y-2", className)}>
      {label && <Label required={required}>{label}</Label>}
      {children}
      {error && (
        <p className="apex-text-sm apex-text-destructive">{error}</p>
      )}
    </div>
  );
}
